import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  Box,
  HStack,
  VStack,
} from '@chakra-ui/react'

function formatSubmittedAt(value: string | null | undefined): string {
  if (!value) return '–'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  const day = d.getDate()
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const month = months[d.getMonth()]
  const year = d.getFullYear()
  const hours = d.getHours()
  const minutes = d.getMinutes()
  const ampm = hours >= 12 ? 'PM' : 'AM'
  const h12 = hours % 12 || 12
  const minStr = minutes < 10 ? `0${minutes}` : String(minutes)
  return `${day} ${month} ${year} - ${h12}:${minStr} ${ampm}`
}

function formatSource(value: string | null | undefined): string {
  if (!value) return '–'
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

type Props = {
  isOpen: boolean
  onClose: () => void
  row: Record<string, unknown> | null
  /** When true, show a Delete control (soft delete is implemented by the parent). */
  showDelete?: boolean
  onDelete?: () => void
  isDeleting?: boolean
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box>
      <Text fontSize="xs" color="gray.500" fontWeight="600" mb={1.5}>
        {label}
      </Text>
      <Text fontSize="sm" lineHeight="short">
        {value ?? '–'}
      </Text>
    </Box>
  )
}

/** Prefer HTML raw_message; fall back to plain from payload / ingestion. */
function resolveEmailBody(row: Record<string, unknown>): string | null {
  const raw = row.raw_message
  if (typeof raw === 'string' && raw.trim() !== '') return raw

  const payload = row.raw_payload
  if (payload != null && typeof payload === 'object' && !Array.isArray(payload)) {
    const p = payload as Record<string, unknown>
    const ingestion = p._ingestion
    if (ingestion != null && typeof ingestion === 'object' && !Array.isArray(ingestion)) {
      const plain = (ingestion as Record<string, unknown>).body_plain
      if (typeof plain === 'string' && plain.trim() !== '') return plain
    }
  }

  return null
}

function looksLikeHtml(s: string): boolean {
  return /<[a-z][\s\S]*>/i.test(s)
}

/** Wrapped HTML for iframe srcDoc; kept out of JSX to avoid `}` in CSS breaking the parser. */
function buildEmailIframeSrcDoc(emailHtml: string): string {
  const safeBody = emailHtml.replace(/<\/script/gi, '<\\/script')
  const csp =
    "default-src 'none'; img-src https: data: cid:; style-src 'unsafe-inline'; base-uri 'none'"
  const css =
    'body{font-family:system-ui,-apple-system,sans-serif;font-size:14px;line-height:1.5;margin:12px;color:#111;word-break:break-word;} img{max-width:100%;height:auto;}'
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${csp}"><style>${css}</style></head><body>${safeBody}</body></html>`
}

export function TicketRequestDetailModal({
  isOpen,
  onClose,
  row,
  showDelete = false,
  onDelete,
  isDeleting = false,
}: Props) {
  if (!row) return null

  const requestNumber = (row.request_number as string) ?? '–'
  const clientName = (row.client_name ?? row.contact_name) as string | undefined
  const requestType = row.service_type
  const requestTypeLabel =
    typeof requestType === 'object' && requestType !== null && 'name' in requestType
      ? (requestType as { name?: string }).name
      : requestType != null
        ? String(requestType)
        : '–'
  const source = formatSource(row.request_source as string | undefined)
  const submittedAt = formatSubmittedAt(row.submitted_at as string | undefined)
  const acknowledgedAt = formatSubmittedAt(row.acknowledged_at as string | undefined)
  const contactEmail = (row.contact_email as string) ?? '–'
  const extractedFields =
    row.extracted_fields != null && typeof row.extracted_fields === 'object'
      ? (row.extracted_fields as Record<string, unknown>)
      : null
  const subjectRaw =
    (row.email_subject as string | undefined) ??
    (row.subject as string | undefined) ??
    (extractedFields?.email_subject as string | undefined) ??
    (extractedFields?.subject_normalized as string | undefined)
  const subject =
    subjectRaw != null && String(subjectRaw).trim() !== '' ? String(subjectRaw).trim() : '–'

  const emailBody = resolveEmailBody(row)
  const emailBodyIsHtml = emailBody != null && looksLikeHtml(emailBody)

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent
        h={{ base: '100dvh', sm: '90dvh' }}
        maxH={{ base: '100dvh', sm: '90dvh' }}
        w={{ base: '100vw', md: '95vw' }}
        maxW="min(1280px, 95vw)"
        display="flex"
        flexDirection="column"
        my={{ base: 0, sm: 'auto' }}
        borderRadius={{ base: 0, sm: 'md' }}
      >
        <ModalHeader flexShrink={0} py={5} px={6} pr={14}>
          Request Details
        </ModalHeader>
        <ModalCloseButton top={4} right={4} />
        <ModalBody
          flex="1"
          minH={0}
          overflow="hidden"
          display="flex"
          flexDirection="column"
          px={6}
          pb={4}
        >
          <VStack align="stretch" spacing={6} flexShrink={0}>
            <HStack
              align="flex-start"
              spacing={{ base: 6, md: 10 }}
              flexDir={{ base: 'column', md: 'row' }}
            >
              <VStack align="stretch" flex="1" spacing={4} w={{ base: 'full', md: 'auto' }}>
                <DetailRow label="Request Number" value={requestNumber} />
                <DetailRow label="Request Type" value={requestTypeLabel} />
                <DetailRow label="Submitted At" value={submittedAt} />
                <DetailRow label="Contact Email" value={contactEmail} />
              </VStack>
              <VStack align="stretch" flex="1" spacing={4} w={{ base: 'full', md: 'auto' }}>
                <DetailRow label="Client" value={clientName ?? '–'} />
                <DetailRow label="Source" value={source} />
                <DetailRow
                  label="Acknowledged At"
                  value={row.acknowledged_at ? acknowledgedAt : '–'}
                />
              </VStack>
            </HStack>
            <DetailRow
              label="Subject"
              value={<Text fontSize="sm" whiteSpace="pre-wrap">{subject}</Text>}
            />
          </VStack>
          {emailBody != null && (
            <Box flex="1" minH={0} display="flex" flexDirection="column" mt={6}>
              <Text fontSize="xs" color="gray.500" fontWeight="600" mb={1.5} flexShrink={0}>
                Request details
              </Text>
              {emailBodyIsHtml ? (
                <Box
                  flex="1"
                  minH={{ base: '200px', md: '240px' }}
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  overflow="hidden"
                  bg="gray.50"
                  _dark={{ bg: 'gray.900', borderColor: 'gray.600' }}
                >
                  <iframe
                    title="Request details"
                    sandbox=""
                    srcDoc={buildEmailIframeSrcDoc(emailBody)}
                    style={{
                      width: '100%',
                      height: '100%',
                      minHeight: '200px',
                      border: 'none',
                      display: 'block',
                    }}
                  />
                </Box>
              ) : (
                <Box flex="1" minH={0} overflowY="auto">
                  <Text fontSize="sm" whiteSpace="pre-wrap">
                    {emailBody}
                  </Text>
                </Box>
              )}
            </Box>
          )}
          {emailBody == null && row.request_source === 'email' && (
            <Box flexShrink={0} mt={6}>
              <Text fontSize="xs" color="gray.500" fontWeight="600" mb={1.5}>
                Request details
              </Text>
              <Text fontSize="sm" color="gray.500">
                No raw message stored for this request.
              </Text>
            </Box>
          )}
        </ModalBody>
        <ModalFooter
          flexShrink={0}
          borderTopWidth="1px"
          borderColor="gray.100"
          _dark={{ borderColor: 'whiteAlpha.200' }}
          py={4}
          px={6}
        >
          <HStack justify="space-between" w="100%">
            <Box>
              {showDelete && onDelete ? (
                <Button
                  colorScheme="red"
                  variant="outline"
                  onClick={onDelete}
                  isLoading={isDeleting}
                  isDisabled={isDeleting}
                >
                  Delete
                </Button>
              ) : null}
            </Box>
            <Button onClick={onClose}>Close</Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
